import { Suspense, useMemo, useCallback, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Tabs, TabsList, TabsTrigger, TabsContentKeepAlive, usePrefetchTab } from '../../components/Tabs'
import { GroupHeader, GroupHeaderSkeleton } from '../../features/group/GroupHeader'
import { GroupAppDataTab } from '../../features/group/tabs/GroupAppDataTab'
import { GroupMembersTab } from '../../features/group/tabs/GroupMembersTab'
import { GroupMessagesTab } from '../../features/group/tabs/GroupMessagesTab'
import { GroupMediaTab } from '../../features/group/tabs/GroupMediaTab'
import { GroupAISummaryTab } from '../../features/group/tabs/GroupAISummaryTab'
import { GroupOCRTab } from '../../features/group/tabs/GroupOCRTab'
import { useGroupDetail, groupDetailQueryKey } from '../../features/group/hooks/useGroupDetail'
import { groupAppDataQueryKey } from '../../features/group/hooks/useGroupAppData'
import { groupMembersQueryKey } from '../../features/group/hooks/useGroupMembers'
import { groupMessagesQueryKey } from '../../features/group/hooks/useGroupMessages'
import { groupMediaQueryKey } from '../../features/group/hooks/useGroupMedia'
import { groupSummaryQueryKey } from '../../features/group/hooks/useGroupSummary'
import { groupOCRQueryKey } from '../../features/group/hooks/useGroupOCR'
import { LazyGroupMiniGraph } from '../../components/graph/lazy'
import { Button } from '../../components/Button'
import { Skeleton } from '../../components/Skeleton'
import { toastSuccess, toastError } from '../../hooks/useToast'
import { api } from '../../lib/api'
import { queryClient } from '../../lib/query-client'

/* ───── Tab query config ───── */

const TAB_STALE_TIME = 60_000
const TAB_GC_TIME = 300_000

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/groups/$id')({
  component: GroupDetailPage,
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: groupDetailQueryKey(params.id),
      queryFn: ({ signal }) =>
        api(`/v1/groups/${params.id}`, { signal }),
      staleTime: 2 * 60 * 1000,
    })
  },
})

/* ───── Page Component ───── */

function GroupDetailPage() {
  const { id } = Route.useParams()
  const { group, isLoading, error } = useGroupDetail(id)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [linkCopied, setLinkCopied] = useState(false)

  const handleCopyGroupLink = useCallback(async () => {
    try {
      const url = `${window.location.origin}/groups/${id}`
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
      toastSuccess('Group link copied to clipboard')
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      toastError('Failed to copy link')
    }
  }, [id])

  const prefetchSimpleTab = useCallback(
    (queryKey: readonly unknown[], endpoint: string) => {
      queryClient.prefetchQuery({
        queryKey,
        queryFn: ({ signal }) => api(endpoint, { signal }),
        staleTime: TAB_STALE_TIME,
        gcTime: TAB_GC_TIME,
      })
    },
    [queryClient],
  )

  const prefetchMap = useMemo(
    () => ({
      'app-data': () =>
        prefetchSimpleTab(groupAppDataQueryKey(id), `/v1/groups/${id}/app-data`),
      members: () =>
        prefetchSimpleTab(groupMembersQueryKey(id), `/v1/groups/${id}/members`),
      messages: () =>
        prefetchSimpleTab(groupMessagesQueryKey(id), `/v1/groups/${id}/messages`),
      media: () =>
        prefetchSimpleTab(groupMediaQueryKey(id), `/v1/groups/${id}/media`),
      'ai-summary': () =>
        prefetchSimpleTab(groupSummaryQueryKey(id), `/v1/groups/${id}/summary`),
      ocr: () =>
        prefetchSimpleTab(groupOCRQueryKey(id), `/v1/groups/${id}/ocr`),
    }),
    [id, prefetchSimpleTab],
  )

  const { handlers: prefetchHandlers } = usePrefetchTab(prefetchMap)

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-6 text-center max-w-md">
          <h2 className="text-lg font-semibold text-[var(--color-danger)]">Group Not Found</h2>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            The requested group could not be loaded. It may have been removed or you may not have access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" data-testid="group-detail-page">

      {/* ━━━ Zone 1: Command Bar (breadcrumb + actions) ━━━ */}
      <div className="flex items-center justify-between gap-4 px-6 py-2.5 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <nav className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] min-w-0" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/dashboard" className="hover:text-[var(--color-text-primary)] transition-colors whitespace-nowrap">Home</Link>
          <span className="text-[var(--color-text-muted)]" aria-hidden="true">/</span>
          <Link to="/groups" className="hover:text-[var(--color-text-primary)] transition-colors whitespace-nowrap">Groups</Link>
          <span className="text-[var(--color-text-muted)]" aria-hidden="true">/</span>
          <span className="text-[var(--color-text-primary)] font-medium truncate max-w-[200px]">
            {group?.name ?? id}
          </span>
        </nav>
        {!isLoading && group && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopyGroupLink}
              data-testid="copy-group-link"
              aria-label="Copy shareable link to this group"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              {linkCopied ? 'Copied!' : 'Copy Link'}
            </Button>
            <div className="w-px h-5 bg-[var(--color-border)] mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/investigations', search: { group: id } })}
              data-testid="open-investigation-button"
              aria-label="Open this group in Investigation Board"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="3" />
                <circle cx="5" cy="19" r="3" />
                <circle cx="19" cy="19" r="3" />
                <line x1="12" x2="5" y1="8" y2="16" />
                <line x1="12" x2="19" y1="8" y2="16" />
              </svg>
              Investigate
            </Button>
          </div>
        )}
      </div>

      {/* ━━━ Zone 2: Profile Card (header + mini-graph) ━━━ */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        {isLoading || !group ? (
          <GroupHeaderSkeleton />
        ) : (
          <div className="flex gap-6 px-6 pt-5 pb-4">
            <div className="flex-1 min-w-0">
              <GroupHeader group={group} />
            </div>
            <div className="shrink-0 hidden lg:flex flex-col items-end" data-testid="group-header-mini-graph">
              <Suspense fallback={<Skeleton className="w-[320px] h-[200px] rounded-lg" />}>
                <LazyGroupMiniGraph
                  groupId={id}
                  width={320}
                  height={200}
                  className="rounded-lg border border-[var(--color-border)]"
                />
              </Suspense>
            </div>
          </div>
        )}
      </div>

      {/* ━━━ Zone 3: Intelligence Tabs ━━━ */}
      <div className="flex-1 overflow-auto" data-testid="responsive-container">
        <Tabs defaultValue="app-data" className="h-full">
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6">
            <TabsList>
              <TabsTrigger value="app-data" onMouseEnter={prefetchHandlers['app-data']}>App Data</TabsTrigger>
              <TabsTrigger value="messages" onMouseEnter={prefetchHandlers['messages']}>Messages</TabsTrigger>
              <TabsTrigger value="members" onMouseEnter={prefetchHandlers['members']}>Members</TabsTrigger>
              <TabsTrigger value="media" onMouseEnter={prefetchHandlers['media']}>Media</TabsTrigger>
              <TabsTrigger value="ai-summary" onMouseEnter={prefetchHandlers['ai-summary']}>AI Summary</TabsTrigger>
              <TabsTrigger value="ocr" onMouseEnter={prefetchHandlers['ocr']}>OCR Results</TabsTrigger>
              <TabsTrigger value="network">Network</TabsTrigger>
            </TabsList>
          </div>

          <div className="px-6 py-4">
            <TabsContentKeepAlive value="app-data">
              <GroupAppDataTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="messages">
              <GroupMessagesTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="members">
              <GroupMembersTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="media">
              <GroupMediaTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="ai-summary">
              <GroupAISummaryTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="ocr">
              <GroupOCRTab groupId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="network">
              <Suspense fallback={<Skeleton className="w-full h-[400px] rounded-lg" />}>
                <LazyGroupMiniGraph
                  groupId={id}
                  width={undefined}
                  height={400}
                  className="w-full"
                />
              </Suspense>
            </TabsContentKeepAlive>
          </div>
        </Tabs>
      </div>
    </div>
  )
}
